import { ArticleHeader, ShareArticle } from "components/article-components"
import {
  BlogAdElement,
  BlogInfoBox,
  BlogTeaserText,
  VideoInBlog,
} from "components/blog-components"
import { Link, graphql } from "gatsby"

import { Demosuche } from "components/page-components/Ausschreibungsdienste"
import { EvergabeWhitepaper } from "components/page-components/Evergabe"
import { Helmet } from "react-helmet"
import Layout from "components/structure-components/layout"
import { NewsTeaserCardRecommended } from "components/page-components/Teaser-Cards"
import React from "react"
import { RecommendedLexiconPosts } from "components/page-components/Wissen"
import { SEOComponent } from "components/seo-components"
import createStandardGermanDate from "scripts/createStandardGermanDate"
import { getSrc } from "gatsby-plugin-image"
import rehypeReact from "rehype-react"

const VergabeNews = ({ location, data }) => {
  const { newsPost, defaultImage, newestNewsPosts, evergabe_events } = data
  const { htmlAst, frontmatter, related_lexicon_posts, tags } = newsPost
  const {
    article_id,
    axcms_id,
    located,
    description,
    excerpt,
    date,
    slugline,
    teaser,
    thumbnail,
    title,
    target_group,
  } = frontmatter

  const render_htmlAst = new rehypeReact({
    createElement: React.createElement,
    components: {
      h2: (props) => <h2 className="title is-4">{props.children}</h2>,
      h3: (props) => <h3 className="title is-5">{props.children}</h3>,
      table: (props) => (
        <div className="table-container">
          <table className="table">{props.children}</table>
        </div>
      ),
      videoinblog: VideoInBlog,
      blogadelement: (props) => (
        <BlogAdElement
          {...{
            target_group,
            props,
            evergabe_event: evergabe_events?.nodes?.[0],
          }}
        />
      ),
      bloginfobox: BlogInfoBox,
    },
  }).Compiler

  const author = { name: frontmatter.author }

  const display_date = createStandardGermanDate(date)

  const thumbnail_src = getSrc(thumbnail)

  const open_graph_image = {
    url: `https://bi-medien.de${thumbnail_src}`,
    width: thumbnail?.childImageSharp?.gatsbyImageData?.width,
    height: thumbnail?.childImageSharp?.gatsbyImageData?.height,
    alt: title,
  }

  const schema_org_data = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://bi-medien.de${location.pathname}`,
    },
    headline: title,
    description: description || excerpt,
    image: [`https://bi-medien.de${thumbnail_src}`],
    datePublished: date,
    author: {
      "@type": author?.name ? "Person" : "Organization",
      name: author?.name || "B_I MEDIEN",
    },
    publisher: {
      "@type": "Organization",
      name: "B_I MEDIEN",
      logo: {
        "@type": "ImageObject",
        url: `https://bi-medien.de/assets/b-i-medien-publisher-logo.png`,
      },
    },
  }

  const breadcrumb_array = [
    {
      type: "BreadcrumbLink",
      content: `B_I MEDIEN`,
      link: `/`,
      react_key: "footer_breadcrumb_item_1",
      ressort: "mitternachtsblau",
    },
    {
      type: "BreadcrumbLink",
      content: `Vergabe-Wissen`,
      link: `/vergabe-wissen`,
      react_key: "footer_breadcrumb_item_2",
      ressort: "mitternachtsblau",
    },
    {
      type: "BreadcrumbLink",
      content: `News`,
      link: `/vergabe-wissen/news`,
      react_key: "footer_breadcrumb_item_3",
      ressort: "mitternachtsblau",
    },
    {
      type: "BreadcrumbSpan",
      content: title,
      link: ``,
      react_key: "footer_breadcrumb_item_4",
      ressort: "mitternachtsblau",
    },
  ]
  return (
    <Layout {...{ location, breadcrumb_array }}>
      <Helmet>
        <meta name="x-bi-article-type" content="vergabenews" />
        <meta name="x-bi-article-id" content={article_id || axcms_id} />
        {!!tags?.[0] ? (
          <meta
            name="x-bi-content-tags"
            content={JSON.stringify(
              tags?.map(({ short_title }) => short_title)
            )}
          />
        ) : null}
      </Helmet>

      <SEOComponent
        brand={`B_I MEDIEN`}
        title={title}
        description={description || excerpt}
        schema_org_data={schema_org_data}
        open_graph_image={open_graph_image}
      />

      <div className="columns is-centered">
        <div className="column is-half">
          <article className="section">
            <ArticleHeader
              {...{
                located: [located],
                display_date,
                ressort: "mitternachtsblau",
                is_h1: true,
                headline: title,
                human_authors: [author],
              }}
            />

            <div className="content pb-6">
              <div className="blog-wrapper">
                {!!teaser ? <BlogTeaserText>{teaser}</BlogTeaserText> : null}
                {render_htmlAst(htmlAst)}
              </div>
            </div>

            {!!tags?.[0] ? (
              <React.Fragment>
                <p className="has-text-weight-bold has-text-centered mb-4">
                  Interessante Themen:
                </p>
                <div className="buttons is-centered">
                  {tags.map(({ tag, short_title }) => (
                    <Link
                      className="button is-mitternachtsblau is-rounded is-outlined"
                      to={`/vergabe-wissen/thema/${tag}`}
                    >
                      {short_title}
                    </Link>
                  ))}
                </div>
              </React.Fragment>
            ) : null}
            <hr className="has-background-mitternachtsblau-30" />

            <div className="columns is-centered is-vcentered mb-6">
              <div className="column is-6 px-0 pb-0 has-text-centered-mobile">
                <Link to={`/vergabe-wissen/news`} className={`link`}>
                  Alle Nachrichten
                </Link>
              </div>
              <div className="column is-6 px-0">
                <ShareArticle
                  {...{
                    description,
                    seo_title: title,
                    path: location.pathname,
                    ressort: "mitternachtsblau",
                    slugline,
                  }}
                />
              </div>
            </div>

            {!!newestNewsPosts.nodes.length ? (
              <div>
                <h2 className="title is-4 ml-3">
                  Aktuelle Meldungen aus dem Vergaberecht:
                </h2>
                <div className="columns pb-4">
                  {newestNewsPosts.nodes.map((article, index) => (
                    <div className="column" key={`recommende-vnews-${index}`}>
                      <NewsTeaserCardRecommended
                        article={article}
                        defaultImage={defaultImage}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {!!related_lexicon_posts?.[0] ? (
              <RecommendedLexiconPosts {...{ related_lexicon_posts }} />
            ) : null}
          </article>
        </div>
      </div>

      <div className="section has-background-mitternachtsblau-5">
        {target_group === "evergabe" ? (
          <EvergabeWhitepaper />
        ) : (
          <div className="my-6">
            <Demosuche />
          </div>
        )}
      </div>
    </Layout>
  )
}

export default VergabeNews

export const pageQuery = graphql`
  query ($id: String!) {
    newsPost: markdownRemark(id: { eq: $id }) {
      htmlAst
      tags {
        tag
        short_title
      }
      excerpt
      frontmatter {
        teaser
        target_group
        article_id
        axcms_id
        located
        author
        description
        date
        slugline
        title
        thumbnail {
          childImageSharp {
            gatsbyImageData(
              formats: [AUTO, AVIF]
              width: 450
              height: 270
              layout: CONSTRAINED
              placeholder: BLURRED
            )
          }
        }
      }
      related_lexicon_posts {
        excerpt
        frontmatter {
          slugline
          title
          category
        }
      }
    }
    newestNewsPosts: allMarkdownRemark(
      filter: {
        frontmatter: { content_type: { eq: "vergabe_news" } }
        id: { ne: $id }
      }
      sort: { frontmatter: { date: DESC } }
      limit: 3
    ) {
      nodes {
        frontmatter {
          title
          slugline
          date
          thumbnail {
            childImageSharp {
              gatsbyImageData(
                formats: [AUTO, AVIF]
                width: 245
                height: 150
                layout: CONSTRAINED
                placeholder: BLURRED
              )
            }
          }
        }
      }
    }
    evergabe_events: allStrapiEVergabeEvent(
      filter: { title: { regex: "/^(?!.*DUMMY EVENT).*$/" } }
      limit: 1
      sort: { date: ASC }
    ) {
      nodes {
        attendance_mode
        date
        strapi_id
        title
        description
        subtitle
        referent {
          name
          company
          link
          image {
            localFile {
              childImageSharp {
                gatsbyImageData(
                  width: 48
                  height: 48
                  formats: [AUTO, AVIF]
                  layout: CONSTRAINED
                  placeholder: BLURRED
                )
              }
            }
          }
        }
      }
    }
    defaultImage: file(name: { eq: "missing-file" }) {
      childImageSharp {
        gatsbyImageData(
          formats: [AUTO, AVIF]
          width: 245
          height: 150
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
    }
  }
`
